import { FetchQueryOptions, isServer, useSuspenseQuery } from "@tanstack/react-query";

import type { AnnouncementCategory } from "@/app/api/announcements/data";
import { LatestAnnouncementResponse } from "@/app/api/announcements/latest/route";
import type { AnnouncementsResponse } from "@/app/api/announcements/route";
import { APP_URL } from "@/constants/urls";

export type { Announcement, AnnouncementCategory } from "@/app/api/announcements/data";
export type { AnnouncementsResponse } from "@/app/api/announcements/route";

export interface AnnouncementsRequestArgs {
  category?: AnnouncementCategory | null;
}

export const ANNOUNCEMENTS_QUERY_KEY = "announcements";

export function announcementsQueryKey({ category }: AnnouncementsRequestArgs) {
  return [ANNOUNCEMENTS_QUERY_KEY, category] as const;
}

async function fetchAnnouncements(args: AnnouncementsRequestArgs): Promise<AnnouncementsResponse> {
  const params = new URLSearchParams();
  if (args.category) {
    params.set("category", args.category);
  }

  // Use an absolute URL on the server and a relative URL on the client
  const baseUrl = isServer ? APP_URL : "";

  const url = `${baseUrl}/api/announcements${params.toString() ? `?${params.toString()}` : ""}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error("Failed to fetch announcements");
  }

  return response.json() as Promise<AnnouncementsResponse>;
}

export function announcementsQuery(args: AnnouncementsRequestArgs) {
  return {
    queryKey: announcementsQueryKey(args),
    queryFn: () => fetchAnnouncements(args),
  } satisfies FetchQueryOptions;
}

export function useAnnouncements(args: AnnouncementsRequestArgs) {
  return useSuspenseQuery(announcementsQuery(args));
}

export function latestAnnouncementQueryKey() {
  return ["latest-announcement"] as const;
}

async function fetchLatestAnnouncement(): Promise<LatestAnnouncementResponse | null> {
  // Use an absolute URL on the server and a relative URL on the client
  const baseUrl = isServer ? APP_URL : "";

  const response = await fetch(`${baseUrl}/api/announcements/latest`);
  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    throw new Error("Failed to fetch latest announcement");
  }

  return response.json() as Promise<LatestAnnouncementResponse>;
}

export function latestAnnouncementQuery() {
  return {
    queryKey: latestAnnouncementQueryKey(),
    queryFn: () => fetchLatestAnnouncement(),
  } satisfies FetchQueryOptions;
}

export function useLatestAnnouncement() {
  return useSuspenseQuery(latestAnnouncementQuery());
}
